import React from 'react';
import PropTypes from 'prop-types';

import PopUp from '../../Popup';
import CheckoutForm from '../../CheckoutForm';
import SubscriptionSummary from '../../BillingForm/SubscriptionSummary';
import { stripeCheckoutFormText } from '../../../utils/constants';
import { getUpdateCcContextCopy } from '../../../utils/updateCcCopy';

import styles from './updateCCPopUp.scss';

// Per-element loading skeleton — one shimmer bar per line the loaded modal shows (title, each summary
// line, the copy, each card input, both buttons), sized/positioned to match so the real content drops
// in with no layout shift.
const SkeletonField = () => (
    <div className={styles.skField}>
        <span className={`${styles.shimmer} ${styles.skLabel}`} />
        <span className={`${styles.shimmer} ${styles.skFieldInput}`} />
    </div>
);

const LoadingSkeleton = ({ showSummary, 'data-testid': dataTestId }) => (
    <div className={styles.skeleton} data-testid={dataTestId && `${dataTestId}.loading`}>
        <span className={`${styles.shimmer} ${styles.skTitle}`} />
        {showSummary && (
            <div className={styles.skSummary}>
                <span className={`${styles.shimmer} ${styles.skEnrolled}`} />
                <span className={`${styles.shimmer} ${styles.skPrice}`} />
                <span className={`${styles.shimmer} ${styles.skRenewal}`} />
            </div>
        )}
        {showSummary && <hr className={styles.divider} />}
        <div className={styles.skCopy}>
            <span className={`${styles.shimmer} ${styles.skCopyLine}`} />
            <span className={`${styles.shimmer} ${styles.skCopyLineShort}`} />
        </div>
        <SkeletonField />
        <SkeletonField />
        <div className={styles.skRow}>
            <SkeletonField />
            <SkeletonField />
        </div>
        <div className={styles.skButtons}>
            <span className={`${styles.shimmer} ${styles.skButton}`} />
            <span className={`${styles.shimmer} ${styles.skButton}`} />
        </div>
    </div>
);

LoadingSkeleton.propTypes = {
    showSummary: PropTypes.bool,
    'data-testid': PropTypes.string,
};

LoadingSkeleton.defaultProps = {
    showSummary: false,
    'data-testid': null,
};

// Shared Update-Credit-Card modal used app-wide (TMS-2338). Standard confirmation-modal chrome (no
// top-right close icon, black bottom border via `cpp-popup-card`) with the vertical order: title,
// subscription summary (off-page only), a divider, the scenario copy, then the card form + centered
// Cancel/Update pair. The close icon is intentionally dropped so the only exits are those buttons.
const UpdateCCPopUp = ({
    onCloseClick,
    onCancel,
    onSuccess,
    subscriptionId,
    forceSubmitting,
    isResume,
    isResolvingSubscription,
    showSubscriptionSummary,
    subscription,
    isMonthly,
    isAppleSubscriber,
    'data-testid': dataTestId,
}) => (
    <div>
        <PopUp
            className="popup-md"
            popUpClassName="cpp-popup-card"
            config={{
                noPadding: false,
                fullScreen: true,
                showCloseIcon: false,
                style: {
                    maxHeight: '80vh',
                },
            }}
            onCloseClick={onCloseClick}
            data-testid={dataTestId}
        >
            {isResolvingSubscription ? (
                <LoadingSkeleton showSummary={showSubscriptionSummary} data-testid={dataTestId} />
            ) : (
                <div className={styles.body}>
                    <div className={styles.title} data-testid={dataTestId && `${dataTestId}.title`}>
                        {stripeCheckoutFormText.cc_update_title}
                    </div>
                    {showSubscriptionSummary && (
                        <div className={styles.summary}>
                            <SubscriptionSummary
                                data-testid={dataTestId && `${dataTestId}.subscription-summary`}
                                subscription={subscription}
                                isMonthly={isMonthly}
                                isAppleSubscriber={isAppleSubscriber}
                            />
                        </div>
                    )}
                    {showSubscriptionSummary && (
                        <hr className={styles.divider} data-testid={dataTestId && `${dataTestId}.divider`} />
                    )}
                    <div className={styles.contextCopy} data-testid={dataTestId && `${dataTestId}.context-copy`}>
                        {getUpdateCcContextCopy(isResume)}
                    </div>
                    <CheckoutForm
                        formType="cc_update"
                        subscriptionId={subscriptionId}
                        onComplete={onSuccess}
                        forceSubmitting={forceSubmitting}
                        onCancel={onCancel || onCloseClick}
                    />
                </div>
            )}
        </PopUp>
    </div>
);

UpdateCCPopUp.propTypes = {
    onCloseClick: PropTypes.func.isRequired,
    onCancel: PropTypes.func,
    onSuccess: PropTypes.func.isRequired,
    subscriptionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    forceSubmitting: PropTypes.bool,
    isResume: PropTypes.bool,
    isResolvingSubscription: PropTypes.bool,
    showSubscriptionSummary: PropTypes.bool,
    subscription: PropTypes.shape({}),
    isMonthly: PropTypes.bool,
    isAppleSubscriber: PropTypes.bool,
    'data-testid': PropTypes.string,
};

UpdateCCPopUp.defaultProps = {
    onCancel: null,
    subscriptionId: null,
    forceSubmitting: false,
    isResume: false,
    isResolvingSubscription: false,
    showSubscriptionSummary: false,
    subscription: null,
    isMonthly: false,
    isAppleSubscriber: false,
    'data-testid': 'modal.update-cc',
};

export default UpdateCCPopUp;
